import { useEffect, useMemo, useState } from "react";
import Seo from "@/lib/seo/Seo";
import { getSeoForPath } from "@/lib/seo/routes.mjs";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Check,
  X,
  Boxes,
  Zap,
  Sliders,
  Lock,
  TrendingDown,
  Workflow,
  ChevronRight,
  Utensils,
  Store,
  ShoppingCart,
  Flame,
  ShoppingBag,
  Scissors,
  Wrench,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import ParticleField from "@/components/ParticleField";
import { EcosystemIllustration } from "@/components/illustrations/FintechIllustrations";
import POSImage from "@/components/POSImage";
import { POS_SYSTEMS, POS_BRANDS } from "@/lib/pos-systems";

/* ────────────────────────────────────────────────────────────
   Brand tab — full-width tab with downward arrow when active.
   Sits at the TOP of the catalog container (same pattern as Terminals).
   ──────────────────────────────────────────────────────────── */
function BrandTab({ brand, isActive, onClick, isFirst, isLast }) {
  return (
    <button
      type="button"
      onClick={() => onClick(brand.id)}
      data-testid={`pos-tab-${brand.id}`}
      role="tab"
      aria-selected={isActive}
      className={`relative flex-1 text-center py-6 sm:py-7 px-4 transition-colors duration-200 outline-none focus-visible:ring-2 focus-visible:ring-[var(--swipe-accent)]/40 ${
        isActive ? "text-white" : "text-[var(--swipe-navy)] hover:text-[var(--swipe-accent)] hover:bg-[var(--swipe-accent-soft)]/60 bg-white"
      } ${!isLast ? "border-r border-line" : ""} ${isFirst && !isActive ? "rounded-tl-3xl" : ""} ${isLast && !isActive ? "rounded-tr-3xl" : ""}`}
      style={
        isActive
          ? {
              background: "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)",
              borderRight: "none",
            }
          : undefined
      }
    >
      <span className="font-display-tight text-[1.05rem] sm:text-[1.25rem] font-extrabold tracking-tight uppercase">
        {brand.name}
      </span>

      {isActive && (
        <span
          aria-hidden="true"
          className="absolute left-1/2 -translate-x-1/2 -bottom-[10px] w-0 h-0"
          style={{
            borderLeft: "10px solid transparent",
            borderRight: "10px solid transparent",
            borderTop: "10px solid #1e90ff",
          }}
        />
      )}
    </button>
  );
}

/* ────────────────────────────────────────────────────────────
   POS card — centered layout.
   ──────────────────────────────────────────────────────────── */
function POSCard({ system, onOpen }) {
  return (
    <div
      data-testid={`pos-card-${system.slug}`}
      className="group relative bg-white border border-line rounded-2xl overflow-hidden transition-all duration-500 hover:-translate-y-2 hover:border-[var(--swipe-accent)] hover:shadow-[0_40px_70px_-25px_rgba(0,114,255,0.32)] flex flex-col text-center"
    >
      {/* Top accent bar — reveals on hover */}
      <span
        aria-hidden="true"
        className="absolute top-0 left-0 right-0 h-[3px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 z-10"
        style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 50%, #061A3A 100%)" }}
      />

      <button
        type="button"
        onClick={() => onOpen(system)}
        className="relative aspect-[4/3] overflow-hidden focus:outline-none"
        aria-label={`Open ${system.name} details`}
      >
        {/* Soft accent glow behind image on hover */}
        <span
          aria-hidden="true"
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 70% 70% at 50% 50%, rgba(0,114,255,0.15) 0%, transparent 70%)",
          }}
        />
        <POSImage
          system={system}
          className="transition-transform duration-700 group-hover:scale-[1.08]"
        />
      </button>

      <div className="px-6 pt-6 pb-7 flex-1 flex flex-col items-center">
        <h3 className="font-display-tight text-[20px] font-extrabold text-[var(--swipe-navy)] leading-tight transition-colors duration-300 group-hover:text-[var(--swipe-accent)]">
          {system.name}
        </h3>
        <p className="mt-3 text-[14px] text-slate-600 leading-[1.7] line-clamp-4 min-h-[6rem]">
          {system.description}
        </p>

        <div className="mt-auto pt-7 w-full flex flex-col gap-2.5">
          <button
            type="button"
            onClick={() => onOpen(system)}
            className="w-full px-5 py-2.5 rounded-xl border border-line text-[12.5px] font-bold text-[var(--swipe-navy)] hover:bg-[var(--swipe-navy)] hover:text-white hover:border-[var(--swipe-navy)] transition-all"
            data-testid={`learn-more-${system.slug}`}
          >
            Learn More
          </button>
          <Link
            to="/contact"
            className="w-full block px-5 py-3 rounded-xl text-[13px] font-bold text-white tracking-wide uppercase transition-all duration-300 group-hover:shadow-[0_18px_36px_-10px_rgba(0,114,255,0.65)]"
            style={{ background: "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)" }}
            data-testid={`request-quote-${system.slug}`}
          >
            Request POS Quote
          </Link>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   Detail modal.
   ──────────────────────────────────────────────────────────── */
function POSModal({ system, onClose }) {
  useEffect(() => {
    if (!system) return;
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [system, onClose]);

  if (!system) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-6"
      data-testid="pos-modal"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-[var(--swipe-navy)]/70 backdrop-blur-sm" />
      <div
        className="relative w-full sm:max-w-5xl bg-white sm:rounded-3xl shadow-2xl overflow-hidden max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white border border-line text-[var(--swipe-navy)] flex items-center justify-center hover:bg-[var(--swipe-navy)] hover:text-white hover:border-[var(--swipe-navy)] transition-all shadow"
          data-testid="pos-modal-close"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="overflow-y-auto">
          <div className="grid lg:grid-cols-[1fr,1.1fr] gap-0">
            <div
              className="relative h-[300px] sm:h-[400px] lg:h-auto lg:min-h-[560px]"
              style={{ background: "linear-gradient(180deg, #EAF3FF 0%, #EEF2F6 100%)" }}
            >
              <POSImage system={system} className="!h-full p-6" />
            </div>

            <div className="p-7 sm:p-9 lg:p-12">
              <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)]">
                {system.brandName}
              </div>
              <h2 className="mt-2 font-display-tight text-[1.85rem] sm:text-[2.25rem] font-extrabold text-[var(--swipe-navy)] leading-[1.1]">
                {system.name}
              </h2>
              <p className="mt-4 text-[16px] text-slate-600 leading-[1.7]">{system.description}</p>

              <div className="mt-7">
                <Link
                  to="/contact"
                  className="btn-primary-swipe text-[14px] py-3 px-6"
                  data-testid="pos-modal-cta-request"
                >
                  Request POS Quote <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              <div className="mt-9 space-y-7">
                <div>
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)] mb-2">
                    Overview
                  </div>
                  <p className="text-[15px] text-slate-700 leading-[1.8]">{system.description}</p>
                </div>
                {system.bestFor && (
                  <div className="border-t border-line pt-7">
                    <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)] mb-2">
                      Best For
                    </div>
                    <p className="text-[15px] text-slate-700 leading-[1.8]">{system.bestFor}</p>
                  </div>
                )}
                <div className="border-t border-line pt-7">
                  <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[var(--swipe-accent)] mb-3">
                    Key Features
                  </div>
                  <ul className="grid sm:grid-cols-2 gap-2.5">
                    {system.features.map((f) => (
                      <li
                        key={f}
                        className="flex items-start gap-2 text-[14px] text-slate-700 bg-[var(--swipe-accent-soft)]/50 border border-[var(--swipe-accent)]/15 rounded-lg px-3 py-2"
                      >
                        <Check className="w-4 h-4 mt-0.5 text-[var(--swipe-accent)] shrink-0" strokeWidth={3} />
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────
   MAIN PAGE — 4-tab POS catalog. Clover is the default tab.
   ──────────────────────────────────────────────────────────── */
export default function PosSystems() {
  const [activeBrand, setActiveBrand] = useState("clover");
  const [openSystem, setOpenSystem] = useState(null);

  const brand = useMemo(() => POS_BRANDS.find((b) => b.id === activeBrand), [activeBrand]);
  const brandSystems = useMemo(
    () => POS_SYSTEMS.filter((s) => s.brand === activeBrand),
    [activeBrand]
  );

  return (
    <div data-testid="pos-systems-page">
      <Seo {...getSeoForPath("/pos-systems")} />
      {/* HERO — unified: text + illustration blended into one navy header */}
      <section className="relative bg-[var(--swipe-navy)] text-white overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 grid-faint opacity-30 pointer-events-none" />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 55% 65% at 78% 50%, rgba(0,114,255,0.30) 0%, transparent 65%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 40% 50% at 20% 30%, rgba(0,114,255,0.14) 0%, transparent 60%)",
          }}
        />
        <div aria-hidden="true" className="absolute -top-24 -left-20 w-[420px] h-[420px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.18)", filter: "blur(110px)" }} />
        <div aria-hidden="true" className="absolute -bottom-32 right-[12%] w-[480px] h-[480px] rounded-full pointer-events-none" style={{ background: "rgba(0,114,255,0.16)", filter: "blur(120px)" }} />
        <div aria-hidden="true" className="absolute top-[30%] right-[2%] w-2 h-2 rounded-full bg-[var(--swipe-accent)] opacity-70 pointer-events-none" />
        <div aria-hidden="true" className="absolute top-[18%] left-[40%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-60 pointer-events-none" />
        <div aria-hidden="true" className="absolute bottom-[20%] left-[8%] w-1.5 h-1.5 rounded-full bg-[#7DB6FF] opacity-50 pointer-events-none" />

        <ParticleField
          density={36}
          connectDistance={130}
          className="absolute inset-0 w-full h-full"
        />

        <div className="container-swipe relative pt-14 pb-16 lg:pt-20 lg:pb-24">
          <nav className="flex items-center gap-2 text-[13px] text-white/50 mb-8">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to="/products" className="hover:text-white">Products</Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white/80">POS Systems</span>
          </nav>

          <div className="grid lg:grid-cols-[1.05fr,1fr] gap-10 lg:gap-12 items-center">
            <Reveal>
              <div className="relative">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/8 border border-white/15 text-[10.5px] uppercase tracking-[0.22em] font-bold text-[#7DB6FF] mb-6">
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--swipe-accent)] animate-pulse" />
                  Products
                </div>
                <h1 className="font-display-tight text-[2.75rem] sm:text-[3.75rem] lg:text-[5rem] xl:text-[5.5rem] font-extrabold leading-[1.02] text-white uppercase tracking-[-0.035em]">
                  POS{" "}
                  <span className="text-[var(--swipe-accent)]">Systems</span>
                </h1>
                <p className="mt-7 text-[17px] md:text-[18px] text-white/75 leading-[1.7] max-w-xl">
                  Modern point-of-sale systems for small businesses, restaurants, and retail
                  stores—built to accept payments, manage orders, and support smoother operations.
                </p>
                <div className="mt-9 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    className="btn-primary-swipe text-[15px] py-3.5 px-7"
                    data-testid="pos-hero-cta-quote"
                  >
                    Request POS Quote <ArrowRight className="w-4 h-4" />
                  </Link>
                  <Link
                    to="/savings-analysis"
                    className="btn-outline-light text-[15px] py-3.5 px-7"
                    data-testid="pos-hero-cta-savings"
                  >
                    Free Savings Analysis
                  </Link>
                </div>
              </div>
            </Reveal>

            <Reveal variant="slide-right" delay={120}>
              <div className="relative">
                <EcosystemIllustration
                  transparent
                  className="relative w-full h-auto max-w-[560px] mx-auto"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* INTRO — animated reveal headline + paragraph */}
      <section className="relative bg-white overflow-hidden">
        {/* Subtle accent backdrop */}
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none opacity-60"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 0%, rgba(234,243,255,0.9) 0%, transparent 70%)",
          }}
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-32 left-1/2 -translate-x-1/2 w-[600px] h-[200px] rounded-full pointer-events-none opacity-40"
          style={{ background: "rgba(0,114,255,0.10)", filter: "blur(80px)" }}
        />
        <div className="container-swipe relative pt-6 pb-14 lg:pt-8 lg:pb-16 text-center">
          <Reveal variant="words" as="h2" className="font-display-tight text-[2rem] sm:text-[2.75rem] lg:text-[3.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.08] tracking-[-0.02em] max-w-5xl mx-auto" threshold={0.05}>
            {"Interactive Future All-in-One Point of Sale Systems".split(" ").map((word, i, arr) => (
              <span key={i}>
                <span className="word"><span>{word}</span></span>
                {i < arr.length - 1 ? " " : null}
              </span>
            ))}
          </Reveal>

          {/* Animated accent underline */}
          <Reveal variant="underline" delay={500}>
            <span
              aria-hidden="true"
              className="block mx-auto mt-6 h-[3px] w-24 rounded-full"
              style={{ background: "linear-gradient(90deg, #0072FF 0%, #1e90ff 100%)" }}
            />
          </Reveal>

          <Reveal delay={650}>
            <p className="mt-6 text-[16px] md:text-[17px] text-slate-600 leading-[1.8] max-w-[900px] mx-auto">
              Scale and control your business from anywhere. Swipetronics offers POS systems designed
              for flexibility, commercial-grade reliability, and real business operations across
              industries like retail, grocery, hospitality, restaurants, delis, convenience stores,
              and service businesses. Prefer standalone hardware? Browse our{" "}
              <Link
                to="/products"
                className="text-[var(--swipe-accent)] underline underline-offset-2 hover:text-[#005FE0]"
                data-testid="pos-link-products"
              >
                card readers and payment terminals
              </Link>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* CATALOG — single bordered container with tabs on top, grid below */}
      <section className="relative bg-white" data-testid={`pos-grid-${activeBrand}`}>
        {/* Soft background tint behind the catalog box for separation */}
        <div
          aria-hidden="true"
          className="absolute inset-x-0 top-0 bottom-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(180deg, rgba(234,243,255,0.0) 0%, rgba(234,243,255,0.55) 25%, rgba(234,243,255,0.55) 75%, rgba(234,243,255,0.0) 100%)",
          }}
        />
        <div className="container-swipe relative pb-24 lg:pb-28">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-14 lg:mb-16">
              <div className="overline mb-3 inline-flex justify-center">Browse by Brand</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.15]">
                POS Systems
              </h2>
              <p className="mt-5 text-[15.5px] text-slate-600 leading-[1.8] max-w-[900px] mx-auto">
                <span className="font-bold text-[var(--swipe-navy)]">{brand?.name}: </span>
                {brand?.description}
              </p>
            </div>
          </Reveal>

          {/* One connected catalog container */}
          <div className="border border-line rounded-3xl overflow-hidden bg-white shadow-[0_30px_80px_-40px_rgba(6,26,58,0.15)]">
            {/* TAB BAR — full-width tabs across the top */}
            <div
              className="flex border-b border-line"
              data-testid="pos-tabs"
              role="tablist"
              aria-label="POS brand tabs"
            >
              {POS_BRANDS.map((b, i) => (
                <BrandTab
                  key={b.id}
                  brand={b}
                  isActive={activeBrand === b.id}
                  onClick={setActiveBrand}
                  isFirst={i === 0}
                  isLast={i === POS_BRANDS.length - 1}
                />
              ))}
            </div>

            {/* PRODUCT GRID — directly under tabs, inside same container */}
            <div className="px-5 py-8 sm:px-7 sm:py-10 lg:px-10 lg:py-12">
              <div
                key={`grid-${activeBrand}`}
                className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6"
              >
                {brandSystems.map((s, i) => (
                  <Reveal key={s.slug} delay={(i % 3) * 50}>
                    <POSCard system={s} onOpen={setOpenSystem} />
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* IMPROVING YOUR BUSINESS PROCESSES — 6 feature boxes */}
      <section className="bg-[var(--swipe-accent-soft)]/40 border-y border-[var(--swipe-accent)]/15">
        <div className="container-swipe py-20 lg:py-24">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-14">
              <div className="overline mb-3 inline-flex justify-center">Why a Better POS Matters</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] lg:text-[3rem] font-extrabold text-[var(--swipe-navy)] leading-[1.1]">
                Improving Your Business Processes
              </h2>
              <p className="mt-5 text-[16px] text-slate-600 leading-[1.75] max-w-[780px] mx-auto">
                Swipetronics helps businesses choose POS systems that improve checkout, simplify
                operations, and create a smoother customer experience. The right POS system can
                help your business manage inventory, process transactions faster, support secure
                payments, and adapt to the way your business operates.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
            {[
              { icon: Boxes,        title: "Stock Control",                       text: "Improve inventory management with POS tools that help track products, monitor sales activity, and keep daily operations more organized." },
              { icon: Zap,          title: "Efficient Transactions",              text: "POS systems help speed up checkout, reduce customer wait times, and keep payments moving smoothly during busy hours." },
              { icon: Sliders,      title: "Flexibility to Business Requirements", text: "Modern POS systems can be matched to your business type, workflow, checkout process, and customer experience needs." },
              { icon: Lock,         title: "Protected Transaction Handling",      text: "Secure POS technology helps businesses process payments safely while supporting industry standards and customer data protection." },
              { icon: TrendingDown, title: "Expense Reduction",                   text: "A better POS setup can reduce manual work, improve staff efficiency, and help businesses avoid outdated or unnecessary systems." },
              { icon: Workflow,     title: "Integration Functionalities",         text: "POS systems can connect important business tools, payment options, order flow, and operational features into one easier setup." },
            ].map(({ icon: Icon, title, text }, i) => (
              <Reveal key={title} delay={(i % 3) * 70}>
                <div
                  className="group cursor-pointer h-full relative rounded-2xl p-7 lg:p-8 border border-[var(--swipe-accent)]/20 transition-all duration-300 hover:-translate-y-1.5 hover:border-[var(--swipe-accent)] hover:shadow-[0_28px_56px_-22px_rgba(0,114,255,0.45)] overflow-hidden"
                  style={{
                    background: "linear-gradient(160deg, #EAF3FF 0%, #FFFFFF 55%, #EAF3FF 100%)",
                  }}
                  data-testid={`improve-box-${i}`}
                >
                  {/* Accent left bar */}
                  <span
                    aria-hidden="true"
                    className="absolute left-0 top-6 bottom-6 w-1 rounded-r-full bg-[var(--swipe-accent)] opacity-70 group-hover:opacity-100 group-hover:top-4 group-hover:bottom-4 transition-all duration-300"
                  />
                  {/* Soft glow on hover */}
                  <div
                    aria-hidden="true"
                    className="absolute -top-16 -right-16 w-44 h-44 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                    style={{ background: "rgba(0,114,255,0.22)", filter: "blur(40px)" }}
                  />
                  <div className="relative">
                    <div
                      className="w-14 h-14 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110 group-hover:rotate-[-4deg]"
                      style={{
                        background: "linear-gradient(135deg, #0072FF 0%, #1e90ff 100%)",
                        boxShadow: "0 12px 28px -10px rgba(0,114,255,0.55)",
                      }}
                    >
                      <Icon className="w-7 h-7 text-white" strokeWidth={2.2} />
                    </div>
                    <h3 className="mt-6 font-display text-[18px] font-bold text-[var(--swipe-navy)] leading-tight transition-colors duration-300 group-hover:text-[var(--swipe-accent)]">
                      {title}
                    </h3>
                    <p className="mt-3 text-[14.5px] text-slate-600 leading-[1.75]">{text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES WE SERVE — internal links to industry-specific pages */}
      <section className="section bg-white" data-testid="pos-industries-links">
        <div className="container-swipe">
          <Reveal>
            <div className="max-w-3xl mx-auto text-center mb-10 lg:mb-12">
              <div className="overline mb-3 inline-flex justify-center">Industries We Serve</div>
              <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold text-[var(--swipe-navy)] leading-[1.12]">
                POS setups matched to your business type.
              </h2>
              <p className="mt-5 text-[15.5px] text-slate-600 leading-[1.75] max-w-[820px] mx-auto">
                See the terminals, POS systems, and checkout flows Swipetronics recommends for
                real day-to-day operations in your industry.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 lg:gap-4">
            {[
              { icon: Utensils,     label: "Restaurants",         to: "/industries/restaurants" },
              { icon: Store,        label: "Convenience & Delis", to: "/industries/convenience-stores-delis" },
              { icon: ShoppingCart, label: "Grocery Stores",      to: "/industries/grocery-stores" },
              { icon: Flame,        label: "Smoke Shops",         to: "/industries/smoke-shops" },
              { icon: ShoppingBag,  label: "Retail Stores",       to: "/industries/retail-stores" },
              { icon: Scissors,     label: "Salons & Spas",       to: "/industries/salons-spas" },
              { icon: Wrench,       label: "Service Businesses",  to: "/industries/service-businesses" },
            ].map((ind, i) => {
              const Icon = ind.icon;
              return (
                <Reveal key={ind.to} delay={(i % 7) * 40}>
                  <Link
                    to={ind.to}
                    data-testid={`pos-industry-link-${ind.to.split("/").pop()}`}
                    className="group relative block bg-white border border-line rounded-2xl p-5 text-center transition-all duration-300 hover:-translate-y-1 hover:border-[var(--swipe-accent)] hover:shadow-[0_18px_36px_-18px_rgba(0,114,255,0.35)]"
                  >
                    <div className="w-11 h-11 mx-auto rounded-xl bg-[var(--swipe-accent-soft)] text-[var(--swipe-accent)] flex items-center justify-center transition-all duration-300 group-hover:bg-[var(--swipe-accent)] group-hover:text-white group-hover:scale-110">
                      <Icon className="w-5 h-5" strokeWidth={2.2} />
                    </div>
                    <div className="mt-3 font-display text-[13.5px] font-bold text-[var(--swipe-navy)] leading-tight group-hover:text-[var(--swipe-accent)] transition-colors">
                      {ind.label}
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[var(--swipe-navy)] text-white relative overflow-hidden">
        <div className="absolute inset-0 grid-faint opacity-40 pointer-events-none" />
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(ellipse 60% 50% at 80% 0%, rgba(0,114,255,0.20) 0%, transparent 60%)"
        }} />
        <div className="container-swipe py-16 md:py-20 relative">
          <Reveal>
            <div className="grid lg:grid-cols-[1.4fr,1fr] gap-8 items-center">
              <div>
                <div className="overline mb-3" style={{ color: "#7DB6FF" }}>Not sure which POS?</div>
                <h2 className="font-display-tight text-[1.85rem] sm:text-[2.5rem] font-extrabold text-white leading-tight">
                  Tell us how your business runs and we&apos;ll match the right POS.
                </h2>
                <p className="mt-5 text-[16px] text-white/70 leading-[1.75] max-w-2xl">
                  Counter flow, ticket size, peak hours, integrations — Swipetronics matches the right
                  system to the right workflow, never the other way around.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-3 lg:justify-end">
                <Link to="/contact" className="btn-primary-swipe" data-testid="pos-cta-contact">
                  Request POS Quote <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/savings-analysis" className="btn-outline-light" data-testid="pos-cta-savings">
                  Free Savings Analysis
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <POSModal system={openSystem} onClose={() => setOpenSystem(null)} />
    </div>
  );
}
